/**
 * Shared verification event content components
 *
 * These components handle rendering of verification events (correct, incorrect, reverted)
 * and are shared between event-renderer.tsx and verification-event.tsx to avoid duplication.
 */

export interface VerificationEventDetails {
  auto_restored?: boolean;
  trigger_description?: string;
  previous_value?: number | null;
  new_value?: number | null;
  verified_value?: number | null;
  percentage_change?: number | null;
}

export interface VerificationEventPayload {
  reason?: string;
  details?: VerificationEventDetails | null;
  content?: string | string[];
}

/**
 * Renders content for verification_correct / COMPLETION_CORRECT events
 * @return {JSX.Element} JSX element
 */
export const VerificationCorrectContent = ({
  payload,
}: {
  payload: VerificationEventPayload;
}) => {
  const details = payload.details;

  // Auto-restored verification (value returned to verified value)
  if (details?.auto_restored) {
    const triggerDesc = details.trigger_description;
    const prevVal = details.previous_value;
    const newVal = details.new_value;

    return (
      <div>
        <p>
          Restored: Value returned to verified value ({prevVal} → {newVal})
        </p>
        {triggerDesc && <p>Trigger: {triggerDesc}</p>}
      </div>
    );
  }

  // Manual verification by user
  return (
    <div>
      <p>Status Updated: Correct</p>
      {payload.reason && <p>Reason: {payload.reason}</p>}
    </div>
  );
};

/**
 * Renders content for verification_incorrect / COMPLETION_INCORRECT events
 * @return {JSX.Element} JSX element
 */
export const VerificationIncorrectContent = ({
  payload,
}: {
  payload: VerificationEventPayload;
}) => {
  const details = payload.details;

  // Auto-marked as incorrect due to value change (details is object with trigger_description)
  if (details && typeof details === 'object' && details.trigger_description) {
    const prevVal = details.previous_value;
    const newVal = details.new_value;
    const pctChange = details.percentage_change;
    const pctStr =
      pctChange !== null && pctChange !== undefined
        ? `, ${pctChange >= 0 ? '+' : ''}${pctChange}%`
        : '';

    return (
      <div>
        <p>
          Incorrect: {details.trigger_description} ({prevVal} → {newVal}
          {pctStr})
        </p>
      </div>
    );
  }

  // Manual marking by user (details may be a string with additional info)
  return (
    <div>
      <p>Status Updated: Incorrect</p>
      {payload.reason && <p>Reason: {payload.reason}</p>}
      {details && typeof details === 'string' && <p>{details}</p>}
    </div>
  );
};

/**
 * Renders content for verification_reverted / COMPLETION_REVERTED events
 * @return {JSX.Element} JSX element
 */
export const VerificationRevertedContent = ({
  payload,
}: {
  payload: VerificationEventPayload;
}) => {
  const details = payload.details;
  const reason = payload.reason;

  // Single-line format: "Reverted: Unit geometry updated (0.125 → 0.250, +100%)"
  if (details?.trigger_description) {
    const prevVal = details.previous_value;
    const newVal = details.new_value;
    const pctChange = details.percentage_change;
    const pctStr =
      pctChange !== null && pctChange !== undefined
        ? `, ${pctChange >= 0 ? '+' : ''}${pctChange}%`
        : '';

    return (
      <div>
        <p>
          Reverted: {details.trigger_description} ({prevVal} → {newVal}
          {pctStr})
        </p>
      </div>
    );
  }

  // Fallback for events without details (backward compatibility)
  return (
    <div>
      <p>Status Updated: Reverted</p>
      {reason && <p>{String(reason)}</p>}
    </div>
  );
};
